import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Shield,
  Brain,
  Heart,
  Activity,
  Target,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../../utils/apiClient';

export default function AlignmentDashboard() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [periodHours, setPeriodHours] = useState('24');

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/alignment/report?period_hours=${periodHours}`);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch alignment report:', error);
      toast.error(t('adminAlignment.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [periodHours, t]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReport();
    setRefreshing(false);
    toast.success(t('adminAlignment.reportRefreshed'));
  };

  const getHealthBadge = (status) => {
    const config = {
      HEALTHY: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2, label: t('adminAlignment.healthy') },
      WARNING: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertTriangle, label: t('adminAlignment.warning') },
      CRITICAL: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle, label: t('adminAlignment.critical') },
    };
    const { color, icon: Icon, label } = config[status] || config.HEALTHY;
    return (
      <Badge variant="outline" className={`${color} flex items-center gap-1.5 px-3 py-1`}>
        <Icon className="w-3.5 h-3.5" />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="alignment-dashboard-loading" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="alignment-dashboard" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="p-2 bg-slate-900 rounded-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <h1 className="text-2xl font-bold text-gray-900">{t('adminAlignment.title')}</h1>
              <p className={`text-sm text-gray-500 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Lock className="w-3.5 h-3.5" />
                {t('adminAlignment.internalAdminView')}
              </p>
            </div>
          </div>
        </div>
        
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Select value={periodHours} onValueChange={setPeriodHours}>
            <SelectTrigger className="w-40" data-testid="period-selector">
              <SelectValue placeholder={t('adminAlignment.selectPeriod')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">{t('adminAlignment.last1Hour')}</SelectItem>
              <SelectItem value="24">{t('adminAlignment.last24Hours')}</SelectItem>
              <SelectItem value="168">{t('adminAlignment.last7Days')}</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} ${refreshing ? 'animate-spin' : ''}`} />
            {t('adminAlignment.refresh')}
          </Button>
        </div>
      </div>

      {/* Health Status Banner */}
      <Card className={`border-l-4 border-l-slate-900 ${isRTL ? 'border-l-0 border-r-4 border-r-slate-900' : ''}`}>
        <CardContent className="p-4">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {getHealthBadge(report?.health_status)}
              <div className={isRTL ? 'text-right' : ''}>
                <p className="font-medium text-gray-900">{t('adminAlignment.systemHealth')}</p>
                <p className="text-sm text-gray-500">
                  {t('adminAlignment.reportPeriod')}: {report?.report_period}
                </p>
              </div>
            </div>
            <div className={`text-sm text-gray-500 ${isRTL ? 'text-left' : 'text-right'}`}>
              <Clock className={`w-4 h-4 inline ${isRTL ? 'ml-1' : 'mr-1'}`} />
              {t('adminAlignment.generated')}: {report?.generated_at ? new Date(report.generated_at).toLocaleString(isRTL ? 'ar' : 'en') : 'N/A'}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Executive Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Eye className="w-5 h-5 text-slate-600" />
            {t('adminAlignment.executiveSummary')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className={`text-gray-700 leading-relaxed ${isRTL ? 'text-right' : ''}`} data-testid="executive-summary">
            {report?.executive_summary || t('adminAlignment.noSummaryAvailable')}
          </p>
        </CardContent>
      </Card>

      {/* Main Sections Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section 1: Identity Fidelity */}
        <Card data-testid="identity-fidelity-section">
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Brain className="w-5 h-5 text-indigo-600" />
              {t('adminAlignment.identityFidelity')}
            </CardTitle>
            <CardDescription className={isRTL ? 'text-right' : ''}>
              {t('adminAlignment.identityFidelityDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              <div className={`prose prose-sm max-w-none ${isRTL ? 'text-right' : ''}`}>
                <NarrativeBlock content={report?.identity_fidelity_narrative} noDataText={t('adminAlignment.noDataAvailable')} isRTL={isRTL} />
              </div>
              
              {report?.metrics?.rewrites > 0 && (
                <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                  <p className={`text-sm font-medium text-indigo-900 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Activity className="w-4 h-4" />
                    {t('adminAlignment.patternRewritesApplied', { count: report.metrics.rewrites })}
                  </p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Section 2: Restraint Events */}
        <Card data-testid="restraint-events-section">
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Heart className="w-5 h-5 text-rose-600" />
              {t('adminAlignment.restraintEvents')}
            </CardTitle>
            <CardDescription className={isRTL ? 'text-right' : ''}>
              {t('adminAlignment.restraintEventsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              <div className={`prose prose-sm max-w-none ${isRTL ? 'text-right' : ''}`}>
                <NarrativeBlock content={report?.behavioral_discipline_narrative} noDataText={t('adminAlignment.noDataAvailable')} isRTL={isRTL} />
              </div>
              
              {report?.metrics?.restraints > 0 && (
                <div className="mt-4 p-3 bg-rose-50 rounded-lg">
                  <p className={`text-sm font-medium text-rose-900 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Shield className="w-4 h-4" />
                    {t('adminAlignment.restraintEventsRecorded', { count: report.metrics.restraints })}
                  </p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Section 3: Philosophical Compliance */}
        <Card data-testid="philosophical-compliance-section">
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Shield className="w-5 h-5 text-emerald-600" />
              {t('adminAlignment.philosophicalCompliance')}
            </CardTitle>
            <CardDescription className={isRTL ? 'text-right' : ''}>
              {t('adminAlignment.philosophicalComplianceDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              <div className={`prose prose-sm max-w-none ${isRTL ? 'text-right' : ''}`}>
                <NarrativeBlock content={report?.philosophical_compliance_narrative} noDataText={t('adminAlignment.noDataAvailable')} isRTL={isRTL} />
              </div>
              
              {report?.metrics?.violations > 0 && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className={`text-sm font-medium text-amber-900 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <AlertTriangle className="w-4 h-4" />
                    {t('adminAlignment.violationsBlocked', { count: report.metrics.violations })}
                  </p>
                </div>
              )}
              
              {report?.metrics?.violations === 0 && (
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                  <p className={`text-sm font-medium text-emerald-900 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle2 className="w-4 h-4" />
                    {t('adminAlignment.noViolationsDetected')}
                  </p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Section 4: Drift & Memory Stability */}
        <Card data-testid="drift-memory-section">
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Activity className="w-5 h-5 text-orange-600" />
              {t('adminAlignment.driftMemoryStability')}
            </CardTitle>
            <CardDescription className={isRTL ? 'text-right' : ''}>
              {t('adminAlignment.driftMemoryStabilityDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              <div className={`prose prose-sm max-w-none ${isRTL ? 'text-right' : ''}`}>
                <NarrativeBlock content={report?.drift_analysis_narrative} noDataText={t('adminAlignment.noDataAvailable')} isRTL={isRTL} />
              </div>
              
              <DriftIndicator metrics={report?.metrics} t={t} isRTL={isRTL} />
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Section 5: Simulation Triggers */}
        <Card className="lg:col-span-2" data-testid="simulation-triggers-section">
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Target className="w-5 h-5 text-violet-600" />
              {t('adminAlignment.simulationTriggers')}
            </CardTitle>
            <CardDescription className={isRTL ? 'text-right' : ''}>
              {t('adminAlignment.simulationTriggersDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SimulationMetric 
                label={t('adminAlignment.ethicalQueries')}
                count={report?.metrics?.ethical_simulations || 0}
                icon={Shield}
                color="violet"
                isRTL={isRTL}
              />
              <SimulationMetric 
                label={t('adminAlignment.highImpactQueries')}
                count={report?.metrics?.high_impact_simulations || 0}
                icon={Target}
                color="amber"
                isRTL={isRTL}
              />
              <SimulationMetric 
                label={t('adminAlignment.identitySensitive')}
                count={report?.metrics?.identity_simulations || 0}
                icon={Brain}
                color="indigo"
                isRTL={isRTL}
              />
            </div>
            
            {report?.metrics?.total_simulations > 0 && (
              <div className="mt-4 p-4 bg-violet-50 rounded-lg">
                <p className={`text-sm text-violet-900 ${isRTL ? 'text-right' : ''}`}>
                  <strong>{t('adminAlignment.totalSimulations', { count: report.metrics.total_simulations })}</strong>{' '}
                  {t('adminAlignment.simulationNote')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Insights Section */}
      {report?.insights && report.insights.length > 0 && (
        <Card data-testid="insights-section">
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg ${isRTL ? 'text-right' : ''}`}>{t('adminAlignment.actionableInsights')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {report.insights.map((insight, idx) => (
                <div key={idx} className="p-4 border rounded-lg">
                  <h4 className={`font-medium text-gray-900 ${isRTL ? 'text-right' : ''}`}>{insight.title}</h4>
                  <p className={`text-sm text-gray-600 mt-1 ${isRTL ? 'text-right' : ''}`}>{insight.summary}</p>
                  {insight.recommended_action && (
                    <p className={`text-sm text-indigo-600 mt-2 font-medium ${isRTL ? 'text-right' : ''}`}>
                      {t('adminAlignment.recommendation')}: {insight.recommended_action}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Helper component for narrative blocks
function NarrativeBlock({ content, noDataText, isRTL }) {
  if (!content) {
    return <p className={`text-gray-500 italic ${isRTL ? 'text-right' : ''}`}>{noDataText}</p>;
  }
  
  // Split by paragraphs and render
  const paragraphs = content.split('\n\n').filter(p => p.trim());
  
  return (
    <div className="space-y-2">
      {paragraphs.map((para, idx) => (
        <p key={idx} className={`text-gray-700 text-sm leading-relaxed ${isRTL ? 'text-right' : ''}`}>
          {para}
        </p>
      ))}
    </div>
  );
}

// Helper component for drift indicator
function DriftIndicator({ metrics, t, isRTL }) {
  if (!metrics) return null;
  
  const driftTrend = metrics.drift_trend || 'stable';
  
  const config = {
    strengthening: { icon: TrendingUp, color: 'emerald', labelKey: 'identityStrengthening' },
    stable: { icon: Minus, color: 'slate', labelKey: 'stable' },
    drifting: { icon: TrendingDown, color: 'amber', labelKey: 'potentialDriftDetected' },
  };
  
  const { icon: Icon, color, labelKey } = config[driftTrend] || config.stable;
  
  return (
    <div className={`mt-4 p-3 bg-${color}-50 rounded-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
      <Icon className={`w-4 h-4 text-${color}-600`} />
      <span className={`text-sm font-medium text-${color}-900`}>{t(`adminAlignment.${labelKey}`)}</span>
    </div>
  );
}

// Helper component for simulation metrics
function SimulationMetric({ label, count, icon: Icon, color, isRTL }) {
  return (
    <div className={`p-4 bg-${color}-50 rounded-lg border border-${color}-100`}>
      <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Icon className={`w-4 h-4 text-${color}-600`} />
        <span className={`text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : ''}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold text-${color}-700 ${isRTL ? 'text-right' : ''}`}>{count}</p>
    </div>
  );
}
